
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getProfile } from '@/services/profileService';
import { Profile, AuthContextType } from '@/types/auth';
import * as authService from '@/services/authService';
import { isUserAdmin, isPlatformAdmin, isOrgAdmin } from '@/utils/authUtils';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfileWithRetry = async (userId: string, retries = 3): Promise<Profile | null> => {
    for (let i = 0; i < retries; i++) {
      try {
        console.log(`Fetching profile for user ${userId}, attempt ${i + 1}`);
        const profileData = await getProfile(userId);
        if (profileData) {
          const typedProfile: Profile = {
            ...profileData,
            user_type: profileData.user_type as 'platform_admin' | 'org_admin' | 'team_member'
          };
          console.log('Profile fetched successfully:', typedProfile);
          return typedProfile;
        } else {
          console.log(`Profile not found for user ${userId}, attempt ${i + 1}`);
        }
      } catch (error) {
        console.error(`Error fetching profile (attempt ${i + 1}):`, error);
        if (i === retries - 1) {
          throw error;
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
      }
    }
    return null;
  };

  useEffect(() => {
    let isProcessing = false;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        // Prevent concurrent processing
        if (isProcessing) {
          console.log('Already processing auth state change, skipping...');
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          isProcessing = true;
          
          // Update invitation status for sign in events
          if (event === 'SIGNED_IN') {
            authService.updateInvitationStatusOnSignIn(session.user.id).catch(error => {
              console.error('Error updating invitation status:', error);
            });
          }
          
          // Fetch profile data with retry logic
          fetchProfileWithRetry(session.user.id)
            .then(profile => {
              setProfile(profile);
            })
            .catch(error => {
              console.error('Failed to fetch profile after retries:', error);
              setProfile(null);
            })
            .finally(() => {
              setLoading(false);
              isProcessing = false;
            });
        } else {
          setProfile(null);
          setLoading(false);
          isProcessing = false;
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isProcessing) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        isProcessing = true;
        fetchProfileWithRetry(session.user.id)
          .then(profile => {
            setProfile(profile);
          })
          .catch(error => {
            console.error('Failed to fetch profile after retries:', error);
            setProfile(null);
          })
          .finally(() => {
            setLoading(false);
            isProcessing = false;
          });
      } else {
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      isProcessing = false;
    };
  }, []);

  const handleSignOut = async () => {
    // Clear local state immediately to prevent race conditions
    setUser(null);
    setProfile(null);
    setSession(null);
    
    // The authService.signOut will handle the actual logout and redirection
    await authService.signOut();
  };

  const handleUpdateProfile = async (updates: { full_name?: string }) => {
    if (!user) {
      return { error: { message: 'User not authenticated' } };
    }

    const result = await authService.updateProfile(user.id, updates);
    
    if (!result.error && result.profile) {
      // Type cast user_type to ensure it matches our union type
      const typedProfile: Profile = {
        ...result.profile,
        user_type: result.profile.user_type as 'platform_admin' | 'org_admin' | 'team_member'
      };
      setProfile(typedProfile);
    }

    return { error: result.error };
  };

  const isAdmin = isUserAdmin(profile);
  const isPlatformAdminUser = isPlatformAdmin(profile);
  const isOrgAdminUser = isOrgAdmin(profile);

  const value = {
    user,
    profile,
    session,
    loading,
    signIn: authService.signIn,
    signOut: handleSignOut,
    resetPassword: authService.resetPassword,
    changePassword: authService.changePassword,
    updateProfile: handleUpdateProfile,
    isAdmin,
    isPlatformAdmin: isPlatformAdminUser,
    isOrgAdmin: isOrgAdminUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
